import { useState } from "react";
import {
  FolderFilled,
  FolderOpenOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getDaysOverdueCellStyle } from "shared/helpers";
import { MyTaskExecutionRow } from "shared/types";

interface UseCompleteMyTasksTreeColumnsProps {
  onCompleteTask: (taskExecutionId: number) => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "-";
};

/**
 * Task-row leading icon. Renders the uploaded task image when it loads, and
 * renders nothing when the task has no icon (null/empty) or the image URL fails
 * to load — so we never show a broken-image placeholder.
 */
const TaskIcon = ({ src }: { src?: string | null }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return null;
  }

  return (
    <img
      src={src}
      alt="icon"
      onError={() => setFailed(true)}
      style={{ width: 20, height: 20, objectFit: "contain" }}
    />
  );
};

/**
 * Columns for the hierarchical "Complete my tasks" tree. Section rows show the
 * section name and its total task-execution count; task rows show the execution
 * details plus a "Complete task" action. Mirrors the flat log columns so the
 * page reads the same, just grouped by section like the Task Hub settings tree.
 */
export const useCompleteMyTasksTreeColumns = ({
  onCompleteTask,
}: UseCompleteMyTasksTreeColumnsProps): ColumnsType<MyTaskExecutionRow> => {
  const { t } = useTranslation();

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const isTask = (row: MyTaskExecutionRow) => row.rowType === "task";

  return [
    {
      title: t("taskHub.myTasks.taskSection") || "Task / Section",
      key: "name",
      render: (_, row) => {
        if (row.rowType === "section") {
          const taskCount = row.total ?? 0;
          const sectionCount = row.sectionCount ?? 0;
          const isEmpty = taskCount === 0 && sectionCount === 0;
          return (
            <span className="flex items-center gap-2">
              <FolderFilled className="text-yellow-500 text-base" />
              <span className="font-semibold">{row.name}</span>

              {isEmpty ? (
                <Tag className="!m-0 !text-gray-400 !bg-gray-50 !border-gray-200">
                  {t("taskHub.sections.empty") || "empty"}
                </Tag>
              ) : (
                <span className="flex items-center gap-1">
                  {sectionCount > 0 && (
                    <Tooltip
                      title={
                        t("taskHub.sections.subsectionsTooltip") ||
                        "Subsections"
                      }
                    >
                      <Tag
                        color="gold"
                        className="!m-0 !flex !items-center gap-1"
                      >
                        <FolderOpenOutlined />
                        {sectionCount}
                      </Tag>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={t("taskHub.sections.tasksTooltip") || "Tasks inside"}
                  >
                    <Tag
                      color="blue"
                      className="!m-0 !flex !items-center gap-1"
                    >
                      <FileTextOutlined />
                      {taskCount}
                    </Tag>
                  </Tooltip>
                </span>
              )}
            </span>
          );
        }

        return (
          <span className="flex items-center gap-2">
            <TaskIcon src={row.icon} />
            {row.name}
          </span>
        );
      },
    },
    {
      title: t("taskHub.logTable.expectedTime") || "Expected Time",
      key: "expected",
      render: (_, row) => (isTask(row) ? formatDate(row.expected) : null),
    },
    {
      title: t("taskHub.logTable.executedTime") || "Executed Time",
      key: "executed",
      render: (_, row) => (isTask(row) ? formatDate(row.executed) : null),
    },
    {
      title: t("taskHub.logTable.status") || "Status",
      key: "status",
      render: (_, row) => (isTask(row) ? <Tag>{row.status ?? "-"}</Tag> : null),
    },
    {
      title: t("taskHub.logTable.priority") || "Priority",
      key: "priority",
      align: "center",
      render: (_, row) => (isTask(row) ? row.priority : null),
    },
    {
      title: t("taskHub.logTable.taskLog") || "Task Log",
      key: "message",
      render: (_, row) => (isTask(row) ? row.message?.trim() || "-" : null),
    },
    {
      title: t("taskHub.logTable.daysOverdue") || "Days overdue",
      key: "daysOverdue",
      align: "center",
      render: (_, row) => {
        if (!isTask(row)) return null;
        return row.daysOverdue == null ? "-" : row.daysOverdue;
      },
      onCell: (row) =>
        isTask(row) ? { style: getDaysOverdueCellStyle(row.daysOverdue) } : {},
    },
    {
      title: t("taskHub.myTasks.actions") || "Actions",
      key: "actions",
      align: "center",
      render: (_, row) =>
        isTask(row) && row.id != null ? (
          <Button
            type="primary"
            style={{ backgroundColor: themeColor, borderColor: themeColor }}
            onClick={() => onCompleteTask(row.id as number)}
          >
            {t("taskHub.myTasks.completeTask") || "Complete task"}
          </Button>
        ) : null,
    },
  ];
};
